import SalesCoachTitleCategoryRole from 'App/resources/SalesCoachTitleCategoryRole';
import { Text, Icon, Link, Tooltip, Flex } from '@upsales/components';
import SalesCoachStakeholder from 'Resources/SalesCoachStakeholder';
import { makeCancelable } from 'App/babel/helpers/promise';
import { SlideFade } from '@upsales/components/animations';
import SelectHelper from '../Helpers/SelectHelper';
import logError from 'App/babel/helpers/logError';
import translate from '../Helpers/translate';
import UpSelect from '../Inputs/UpSelect';
import PropTypes from 'prop-types';
import React from 'react';
import ClickableItem from '@upsales/components/ClickableItem';

import { openEditContact } from 'App/components/EditContact/openEditContact';
import './DecisionMakersSidebar.scss';
import AppService from 'App/legacy/AppService';
import EventBus from 'App/services/EventBus';
import StateRouter from 'App/services/StateRouter';

const formatResult = function (contact, clientId) {
	if (!contact.id) {
		return '<i class="grey">' + translate('default.noContact') + '</i>';
	}
	return SelectHelper.clientContactsAndRelations.formatResult(contact, clientId);
};

const matcher = (term, undef, contact) => {
	return contact.name.toUpperCase().indexOf(term.toUpperCase()) >= 0;
};

class DecisionMakersSidebar extends React.PureComponent {
	constructor(props) {
		super(props);

		const t = Tools.$translate;

		this.state = {
			roleMap: {},
			accountContacts: [],
			isExpanded: true
		};
		this.contactListener = EventBus.on('contact.added', this.updateContact.bind(this));

		this.lang = {
			stakeholders: t('default.stakeholder'),
			titleCategories: t('default.titleCategories'),
			less: t('default.showLess'),
			more: t('default.showMore'),
			noTitle: t('default.noTitle'),
			add: t('default.add'),
			selectContact: t('default.selectContact'),
			remove: t('default.remove'),
			addOneMore: t('opportunity.addOneMore'),
			createNewContact: t('opportunity.createNewContact'),
			createNew: t('opportunity.createNew'),
			visitContact: t('ticket.visitContact')
		};
	}

	componentDidMount() {
		this.init(false);
	}

	componentDidUpdate(prevProps) {
		// When you change sales process
		if (prevProps.salesCoachDecisionMaker.salesCoachId !== this.props.salesCoachDecisionMaker.salesCoachId) {
			this.init(true);
		}
		// When you create a call/appointment with a title category contact
		else if (prevProps.newTitleCategories !== this.props.newTitleCategories) {
			this.updateNewTitleCategories(this.props.newTitleCategories);
		}
		// When contacts change - preserve ALL previously seen contacts
		else if (prevProps.contacts !== this.props.contacts) {
			// Build a comprehensive contact list:
			// 1. All contacts from current state (accumulated over time)
			// 2. All contacts currently in stakeholder roles
			// 3. All new contacts from props
			const contactsMap = new Map();

			// Add all existing contacts from state
			this.state.accountContacts.forEach(contact => {
				if (contact && contact.id) {
					contactsMap.set(contact.id, contact);
				}
			});

			// Add all stakeholder contacts
			Object.values(this.state.roleMap).forEach(role => {
				Object.values(role.contactMap).forEach(contact => {
					if (contact && contact.id) {
						contactsMap.set(contact.id, contact);
					}
				});
			});

			// Add all new contacts from props (these have the most up-to-date data)
			this.props.contacts.forEach(contact => {
				if (contact && contact.id) {
					contactsMap.set(contact.id, contact);
				}
			});

			// Convert map to array
			const mergedContacts = Array.from(contactsMap.values());

			// Re-initialize with comprehensive contact list
			if (this.props.salesCoachDecisionMaker?.hasTitleCategories) {
				this.initTitleCategories(
					this.props.titleCategories,
					mergedContacts,
					this.props.salesCoachDecisionMaker.salesCoachId
				);
			} else {
				this.initStakeholders(
					this.props.salesProcessId,
					this.props.stakeholders,
					mergedContacts,
					this.props.salesCoachDecisionMaker?.salesCoachId
				);
			}
		}
	}

	updateContact(e, contact) {
		this.setState({ accountContacts: [...this.state.accountContacts, contact] });
	}

	componentWillUnmount() {
		if (this.contactListener) {
			this.contactListener();
		}
		if (this.cancelablePromiseStakeholdersCoach) {
			this.cancelablePromiseStakeholdersCoach.cancel();
		}
		if (this.cancelablePromiseTitleCategories) {
			this.cancelablePromiseTitleCategories.cancel();
		}
	}

	toggleExpanded() {
		this.setState({ isExpanded: !this.state.isExpanded });
	}

	addContactToggle(tagId) {
		const { roleMap } = this.state;
		const role = roleMap[tagId];
		if (!role) {
			return;
		}

		role.addingContact = !role.addingContact;
		roleMap[tagId] = role;
		this.setState({ roleMap: { ...roleMap } });
	}

	updateNewTitleCategories(newTitleCategories) {
		const { roleMap, updatedContacts } = this.updateRoleMap(newTitleCategories, this.state.roleMap, [
			...this.state.accountContacts
		]);
		// Only update accountContacts if it actually changed
		if (updatedContacts.length !== this.state.accountContacts.length) {
			this.setState({ roleMap: { ...roleMap }, accountContacts: updatedContacts });
		} else {
			this.setState({ roleMap: { ...roleMap } });
		}
	}

	updateRoleMap(newDecisionMakers, roleMap, contacts) {
		const newRoleMap = roleMap;
		const contactsToAdd = [];

		newDecisionMakers.forEach(decisionMaker => {
			if ((decisionMaker.role?.tagId || decisionMaker.tagId) && decisionMaker.contactId) {
				const tagId = decisionMaker.role?.tagId || decisionMaker.tagId;
				const role = newRoleMap[tagId];

				// Look up full contact from contacts array to get all properties including client
				const fullContact = _.find(contacts, { id: decisionMaker.contact.id });
				if (role) {
					role.hasContacts = true;
					// Use fullContact if available, otherwise use the contact from decisionMaker to preserve stakeholders
					// even when their contact is not in the current contacts list
					const contactToUse = fullContact || decisionMaker.contact;
					role.contactMap[decisionMaker.contact.id] = contactToUse;
					newRoleMap[tagId] = role;

					// If contact wasn't found in the contacts list, add it so it's available for re-selection
					if (!fullContact && !_.find(contacts, { id: decisionMaker.contact.id })) {
						contactsToAdd.push(contactToUse);
					}
				}
			}
		});

		// Add any missing contacts to the contacts array for searchability
		const updatedContacts = [...contacts, ...contactsToAdd];

		return { roleMap: newRoleMap, updatedContacts };
	}

	async initTitleCategories(titleCategories = [], contacts = [], salesCoachId) {
		this.cancelablePromiseTitleCategories = makeCancelable(SalesCoachTitleCategoryRole.find({ salesCoachId }));
		const translations = await this.cancelablePromiseTitleCategories.promise;
		const roleMap = {};
		contacts = _.filter(contacts, contact => contact.id);
		translations.data.forEach(role => {
			roleMap[role.tagId] = {
				tagId: role.tagId,
				name: role.value,
				contactMap: {}
			};
		});
		const { roleMap: updatedRoleMap, updatedContacts } = this.updateRoleMap(titleCategories, roleMap, contacts);
		this.setState({ roleMap: { ...updatedRoleMap }, accountContacts: updatedContacts });
	}

	async initStakeholders(salesProcessId, stakeholders = [], contacts = [], salesCoachId) {
		let translations;
		if (salesCoachId) {
			this.cancelablePromiseStakeholdersCoach = makeCancelable(SalesCoachStakeholder.find({ salesCoachId }));
			translations = await this.cancelablePromiseStakeholdersCoach.promise;
			translations.data = translations.data.filter(stakeholder => stakeholder.active);
		}
		const roleMap = {};
		contacts = _.filter(contacts, contact => contact.id);

		translations.data?.forEach(role => {
			roleMap[role.tagId] = {
				tagId: role.tagId,
				name: role.value,
				contactMap: {}
			};
		});

		const { roleMap: updatedRoleMap, updatedContacts } = this.updateRoleMap(stakeholders, roleMap, contacts);
		this.setState({ roleMap: { ...updatedRoleMap }, accountContacts: updatedContacts });
	}

	init(changedSalesProcess) {
		if (this.props.salesCoachDecisionMaker?.hasTitleCategories) {
			this.initTitleCategories(
				changedSalesProcess ? [] : this.props.titleCategories,
				this.props.contacts,
				this.props.salesCoachDecisionMaker.salesCoachId
			);
		} else {
			this.initStakeholders(
				this.props.salesProcessId,
				changedSalesProcess ? [] : this.props.stakeholders,
				this.props.contacts,
				this.props.salesCoachDecisionMaker?.salesCoachId
			);
		}
	}

	updateDecisionMakers() {
		const currentDecisionMakers = _.reduce(
			Object.values(this.state.roleMap),
			(res, role) => {
				const contacts = Object.values(role.contactMap);
				contacts.forEach(contact => {
					res.push({
						contactId: contact.id,
						tagId: role.tagId,
						contact: { id: contact.id, name: contact.name }
					});
				});

				return res;
			},
			[]
		);

		if (this.props.salesCoachDecisionMaker?.hasTitleCategories) {
			this.props.updateTitleCategories(currentDecisionMakers);
		} else {
			this.props.updateStakeholders(currentDecisionMakers);
		}
	}

	addContact(tagId, contact) {
		const roleMap = this.state.roleMap;
		const role = roleMap[tagId];
		if (!role) {
			return;
		}

		// Look up full contact to ensure we have all properties including client, title, etc.
		const fullContact = _.find(this.state.accountContacts, { id: contact.id }) || contact;
		role.contactMap[contact.id] = fullContact;
		role.hasContacts = true;
		roleMap[tagId] = role;

		// Also ensure the contact is in accountContacts for future lookups
		const existsInAccountContacts = _.find(this.state.accountContacts, { id: contact.id });
		const updatedAccountContacts = existsInAccountContacts
			? this.state.accountContacts
			: [...this.state.accountContacts, fullContact];

		this.setState({ roleMap: { ...roleMap }, accountContacts: updatedAccountContacts });
		this.updateDecisionMakers();
	}

	removeContact(tagId, contactId) {
		const roleMap = this.state.roleMap;
		const role = roleMap[tagId];
		if (!role) {
			return;
		}

		delete role.contactMap[contactId];
		if (Object.keys(role.contactMap).length === 0) {
			role.hasContacts = false;
		}
		roleMap[tagId] = role;
		this.setState({ roleMap: { ...roleMap } });

		this.updateDecisionMakers();
	}

	openContactModal(clientId, role) {
		let titleCategory = null;
		if (this.props.salesCoachDecisionMaker?.hasTitleCategories) {
			titleCategory = {
				tagId: role.tagId,
				value: role.name
			};
		}
		openEditContact({ clientId, titleCategory })
			.then(contact => {
				if (this.props.salesCoachDecisionMaker?.hasTitleCategories) {
					if (role.tagId === contact.titleCategory?.tagId) {
						this.addContact(role.tagId, contact);
					}
				} else {
					this.addContact(role.tagId, contact);
				}
			})
			.catch(err => {
				logError(err, 'Could not add the contact');
			});
	}

	renderRoleContacts(role) {
		const { disabled, clientId } = this.props;
		const { accountContacts } = this.state;
		let contacts = Object.values(role.contactMap);
		contacts = _.sortBy(contacts, 'name');
		const customerId = AppService.getCustomerId();

		return contacts.map(contact => {
			// Look up full contact info from accountContacts to get client data
			// If not found, use the contact object itself (preserves stakeholders even when contact not in list)
			const fullContact = _.find(accountContacts, { id: contact.id }) || contact;
			const isRelatedContact = fullContact.client && clientId && fullContact.client.id !== clientId;

			// Build tooltip text with title and company name
			let relatedCompanyTooltip = '';
			if (isRelatedContact && fullContact?.client) {
				// Try multiple sources for the contact's title
				const contactTitle =
					fullContact.titleCategory?.value ||
					fullContact.titleCategory?.title ||
					fullContact.title ||
					contact.titleCategory?.value ||
					contact.title ||
					'';

				relatedCompanyTooltip = contactTitle
					? `${contactTitle} ${translate('default.at')} ${fullContact.client.name}`
					: fullContact.client.name;
			}

			return (
				<Flex
					justifyContent="space-between"
					direction="row"
					alignItems="center"
					key={contact.id}
					className="DecisionMakersSidebar--Role-Contact"
				>
					<Flex direction="row" alignItems="center" gap={12}>
						<Icon name="user" />
						<Flex direction="column" gap={4}>
							<Flex direction="row" alignItems="center" gap={4}>
								<Tooltip title={this.lang.visitContact} position="bottom" distance={20}>
									<Link
										onClick={e => e.stopPropagation()}
										href={StateRouter.href('contact.dashboard', {
											customerId: customerId,
											id: contact.id
										})}
									>
										<Text size="md" color="black">
											{contact.name}
										</Text>
									</Link>
								</Tooltip>
								{isRelatedContact && fullContact?.client ? (
									<Tooltip title={relatedCompanyTooltip} position="right" distance={10}>
										<Icon name="sitemap" size="sm" color="grey-11" />
									</Tooltip>
								) : null}
							</Flex>
							<Text size="sm" color="grey-11" className="DecisionMakersSidebar--Role-Contact_title">
								{role.name}
							</Text>
						</Flex>
					</Flex>

					{disabled ? null : (
						<Tooltip title={this.lang.remove} position="right" distance={30}>
							<ClickableItem
								className="DecisionMakersSidebar--Role-Contact_remove"
								type="link"
								size="sm"
								onClick={() => this.removeContact(role.tagId, contact.id)}
								icon="times"
								iconColor="grey-8"
								borderRadius
							/>
						</Tooltip>
					)}
				</Flex>
			);
		});
	}

	renderRoles() {
		const { roleMap, accountContacts } = this.state;
		const { disabled } = this.props;
		let roles = Object.values(roleMap);
		roles = _.sortBy(roles, 'name');
		return roles.map(role => {
			const roleContacts = Object.values(role.contactMap);
			let atLeastOneIsChosen = false;
			let contactsToChooseFrom = accountContacts;

			let createNewButtonText = this.lang.createNewContact;
			if (this.props.salesCoachDecisionMaker?.hasTitleCategories) {
				contactsToChooseFrom = accountContacts.filter(contact => contact.titleCategory?.tagId === role.tagId);
				createNewButtonText = this.lang.createNew + role.name;
			}
			const staticAddButton = {
				onClick: () => this.openContactModal(this.props.clientId, role),
				label: createNewButtonText
			};
			const availableContacts = _.reduce(
				contactsToChooseFrom,
				(res, contact) => {
					const alreadySelected = _.find(roleContacts, { id: contact.id });
					if (!alreadySelected) {
						res.push(contact);
					} else {
						atLeastOneIsChosen = true;
					}
					return res;
				},
				[]
			);
			const renderedContacts = this.renderRoleContacts(role);
			var eventListeners = {
				'select2-close': () => {
					setTimeout(() => {
						this.addContactToggle(role.tagId);
					}, 0);
				}
			};
			return (
				<div key={role.tagId} className="DecisionMakersSidebar--Role">
					<div className="DecisionMakersSidebar--Role_Content">
						{renderedContacts}
						{!role.addingContact && !atLeastOneIsChosen && (
							<ClickableItem
								type="link"
								onClick={() => this.addContactToggle(role.tagId)}
								disabled={disabled}
								icon="plus"
								iconColor="grey-11"
								borderRadius
								textSize="sm"
								color="grey-11"
								title={this.lang.add + ' ' + role.name}
							/>
						)}
						{role.addingContact && (
							<UpSelect
								className="form-control"
								placeholder={this.lang.selectContact}
								eventListeners={eventListeners}
								dropdownCssClass={'DecisionMakersSidebar--Select2-dropdown'}
								formatResult={contact => formatResult(contact, this.props.clientId)}
								matcher={matcher}
								autoOpen={true}
								autoFocus={true}
								data={availableContacts}
								required={true}
								onChange={e => this.addContact(role.tagId, e.target.added)}
								staticAddButton={staticAddButton}
							/>
						)}
						{atLeastOneIsChosen && !!availableContacts.length && (
							<ClickableItem
								className="DecisionMakersSidebar--Role_Content_more"
								type="link"
								onClick={() => this.addContactToggle(role.tagId)}
								icon="plus"
								iconColor="grey-11"
								textSize="sm"
								color="grey-11"
								title={this.lang.addOneMore}
							/>
						)}
					</div>
				</div>
			);
		});
	}

	render() {
		const { isExpanded, roleMap } = this.state;
		const renderedRoles = this.renderRoles();
		const maxHeight = 112 * Object.values(roleMap).length;

		return (
			<Flex direction="column" className="DecisionMakersSidebar">
				<Flex
					className="DecisionMakersSidebar--Header"
					direction="row"
					justifyContent="space-between"
					alignContent="center"
					onClick={() => this.toggleExpanded()}
				>
					<Text size="sm" bold color="black">
						{this.props.salesCoachDecisionMaker?.hasTitleCategories
							? this.lang.titleCategories
							: this.lang.stakeholders}
					</Text>
					<Tooltip
						type="link"
						position="right"
						distance={20}
						title={isExpanded ? this.lang.less : this.lang.more}
					>
						<Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} onClick={() => this.toggleExpanded()} />
					</Tooltip>
				</Flex>

				<SlideFade visible={isExpanded} direction="top" height maxHeight={maxHeight}>
					<div className="DecisionMakers--Roles">{renderedRoles}</div>
				</SlideFade>
			</Flex>
		);
	}
}

DecisionMakersSidebar.propTypes = {
	salseProcessId: PropTypes.number,
	salesCoachId: PropTypes.number,
	updateStakeholders: PropTypes.func,
	updateTitleCategories: PropTypes.func,
	stakeholders: PropTypes.array,
	titleCategories: PropTypes.array,
	contacts: PropTypes.array,
	clientId: PropTypes.number,
	salesCoachDecisionMaker: PropTypes.object,
	disabled: PropTypes.bool
};

export default DecisionMakersSidebar;
